import React, { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { useAuth } from "../context/AuthContext";
import { auth } from "../firebase";
import { sendEmailVerification, reload } from "firebase/auth";
import { Alert, AlertDescription } from "../components/ui/alert";

// Add this component for code snippets
const CodeSnippet = ({ code }) => (
    <div className="bg-gray-900 text-gray-100 p-4 rounded-md font-mono text-sm overflow-x-auto">
        <pre>{code}</pre>
    </div>
);

export default function Profile() {
    const { currentUser, setCurrentUser } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [showToken, setShowToken] = useState(false);
    const [verificationSent, setVerificationSent] = useState(false);
    const [generatingKey, setGeneratingKey] = useState(false);
    const [keyGenerated, setKeyGenerated] = useState(false);

    useEffect(() => {
        const fetchUserData = async () => {
            if (currentUser?.email) {
                try {
                    if (currentUser.emailVerified) {
                        console.log('return')
                        return;
                    }
                    // Reload the user to get the latest emailVerified status
                    if (auth.currentUser) {
                        await reload(auth.currentUser);
                    }

                    const response = await fetch(`http://localhost:8000/user/${currentUser.email}`);
                    if (!response.ok) {
                        throw new Error('Failed to fetch user data');
                    }
                    const data = await response.json();
                    console.log('called')
                    // Update verification status in backend if it changed
                    if (auth.currentUser && auth.currentUser.emailVerified !== data.emailVerified) {
                        await fetch('http://localhost:8000/update-verification', {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({
                                email: currentUser.email,
                                emailVerified: auth.currentUser.emailVerified
                            }),
                        });
                    }
                } catch (error) {
                    console.error('Error fetching user data:', error);
                    setError('Failed to load user data');
                } finally {
                    setLoading(false);
                }
            }
        };

        fetchUserData();

        // Set up interval to check verification status
        const interval = setInterval(() => {
            if (auth.currentUser && !auth.currentUser.emailVerified) {
                reload(auth.currentUser)
                    .then(() => {
                        if (auth.currentUser.emailVerified) {
                            clearInterval(interval);
                            fetchUserData();
                            console.log('called')

                        }
                    })
                    .catch(err => console.error("Error reloading user:", err));
            } else {
                clearInterval(interval);
            }
        }, 10000); // Check every 10 seconds

        return () => clearInterval(interval);
    }, [currentUser]);

    const handleCopyToken = () => {
        if (currentUser?.token) {
            navigator.clipboard.writeText(currentUser.token);
            alert("API key copied to clipboard!");
        }
    };

    const handleSendVerification = async () => {
        try {
            if (auth.currentUser) {
                await sendEmailVerification(auth.currentUser);
                setVerificationSent(true);
            }
        } catch (error) {
            console.error("Error sending verification email:", error);
            setError("Failed to send verification email");
        }
    };

    const handleGenerateApiKey = async () => {
        setGeneratingKey(true);
        try {
            const response = await fetch('http://localhost:8000/generate-api-key', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: currentUser.email
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to generate API key');
            }

            const data = await response.json();
            setKeyGenerated(true);
            setCurrentUser({ ...currentUser, token: data.token })
        } catch (error) {
            console.error("Error generating API key:", error);
            setError(error.message);
        } finally {
            setGeneratingKey(false);
        }
    };

    const renderAvatar = () => {
        return (
            <Avatar className="h-16 w-16">
                <AvatarImage
                    src={currentUser?.photoURL}
                    className="h-full w-full object-cover rounded-full"
                />
                <AvatarFallback className="bg-indigo-100 text-indigo-600">
                    {currentUser?.name?.charAt(0).toUpperCase() || '?'}
                </AvatarFallback>
            </Avatar>
        );
    };

    const apiDocs = {
        scrapeChunked: {
            endpoint: "/api/v1/scrape-chunked",
            code: `const response = await fetch('http://localhost:8000/api/v1/scrape-chunked', {
    method: 'POST',
    headers: {
        'Content-Type': 'application/json',
        'x-api-key': 'YOUR_API_KEY'
    },
    body: JSON.stringify({
        email: 'your-email@example.com',
        // Single URL
        url: 'https://example.com',
        multipleUrls: false,   // Set to true for multiple URLs
        
        // For multiple URLs (optional)
        urlArr: [             // Use when multipleUrls is true
            'https://example1.com',
            'https://example2.com'
        ],
        
        // Required parameters
        htmlContent: false,    // boolean - include HTML content in response
        mainContent: true,     // boolean - extract main content only
        
        // Optional parameters
        delay: 1000,          // number - delay between requests (ms)
        pagesToCrawl: 1,      // number - number of pages to crawl
        depth: 1,             // number - crawling depth
        excludeTags: [],      // array - HTML tags to exclude
        includeTags: []       // array - HTML tags to include
    })
});

const reader = response.body.getReader();
const decoder = new TextDecoder();

while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    
    // Process the chunked response
    const chunk = decoder.decode(value);
    console.log(chunk);
}`,
            description: "Extract content from single or multiple URLs with streaming response."
        }
    };

    if (loading) {
        return <div className="flex justify-center items-center h-screen">Loading...</div>;
    }

    return (
        <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto mt-8">
                <Card className="mb-6">
                    <CardHeader>
                        <div className="flex items-center space-x-4">
                            {renderAvatar()}
                            <div>
                                <CardTitle>{currentUser?.name || "User"}</CardTitle>
                                <CardDescription>{currentUser?.email}</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

                        {verificationSent && (
                            <Alert className="bg-green-50 border-green-200">
                                <AlertDescription className="text-green-800">
                                    Verification email sent! Please check your inbox.
                                </AlertDescription>
                            </Alert>
                        )}

                        {keyGenerated && (
                            <Alert className="bg-green-50 border-green-200">
                                <AlertDescription className="text-green-800">
                                    API key generated successfully!
                                </AlertDescription>
                            </Alert>
                        )}

                        {/* Email Verification Status */}
                        <div className="border rounded-md p-4">
                            <h3 className="font-medium mb-2">Email Verification</h3>
                            {auth.currentUser?.emailVerified ? (
                                <div className="text-green-600 flex items-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    Email verified
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    <div className="text-amber-600 flex items-center">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                        Email not verified
                                    </div>
                                    <Button
                                        variant="outline"
                                        onClick={handleSendVerification}
                                        disabled={verificationSent}
                                    >
                                        {verificationSent ? "Email Sent" : "Send Verification Email"}
                                    </Button>
                                </div>
                            )}
                        </div>

                        {/* API Key Section */}
                        <div className="border rounded-md p-4">
                            <h3 className="font-medium mb-2">API Key</h3>

                            {!auth.currentUser?.emailVerified ? (
                                <div className="text-gray-600">
                                    Please verify your email to generate an API key.
                                </div>
                            ) : currentUser?.token ? (
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <div className="font-mono text-sm bg-gray-100 p-2 rounded flex-1 overflow-hidden">
                                            {showToken ? currentUser.token : "••••••••••••••••••••••••••••••••"}
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setShowToken(!showToken)}
                                            className="ml-2"
                                        >
                                            {showToken ? "Hide" : "Show"}
                                        </Button>
                                    </div>
                                    <Button
                                        variant="outline"
                                        onClick={handleCopyToken}
                                    >
                                        Copy API Key
                                    </Button>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    <div className="text-gray-600">
                                        No API key generated yet.
                                    </div>
                                    <Button
                                        onClick={handleGenerateApiKey}
                                        disabled={generatingKey}
                                    >
                                        {generatingKey ? "Generating..." : "Generate API Key"}
                                    </Button>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* API Documentation Section */}
                <Card>
                    <CardHeader>
                        <CardTitle>API Documentation</CardTitle>
                        <CardDescription>
                            Learn how to use the API endpoint with your API key
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div className="space-y-4">
                            <div>
                                <h3 className="font-medium text-lg">Endpoint: {apiDocs.scrapeChunked.endpoint}</h3>
                                <p className="text-sm text-gray-600 mt-1">{apiDocs.scrapeChunked.description}</p>
                            </div>

                            <div className="space-y-2">
                                <h4 className="font-medium">Example Usage:</h4>
                                <CodeSnippet code={apiDocs.scrapeChunked.code} />
                            </div>

                            <div className="bg-blue-50 rounded-lg p-6 space-y-4">
                                <div>
                                    <h4 className="font-medium text-blue-800 mb-2">Response Format</h4>
                                    <ul className="list-disc list-inside text-sm text-blue-700 space-y-1">
                                        <li>cleanText: Extracted text content</li>
                                        <li>markdownData: Content in Markdown format</li>
                                        <li>metaData: Page metadata (title, description, etc.)</li>
                                        <li>htmlContent: HTML content (if requested)</li>
                                    </ul>
                                </div>

                                <div>
                                    <h4 className="font-medium text-blue-800 mb-2">Important Notes</h4>
                                    <ul className="list-disc list-inside text-sm text-blue-700 space-y-1">
                                        <li>API key must be included in the x-api-key header</li>
                                        <li>Email must match the account that owns the API key</li>
                                        <li>Response is streamed in chunks for better performance</li>
                                        <li>Set multipleUrls: true when using urlArr for multiple URLs</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
} 